/**
 * PATH: packages/email-hub/src/feature/components/sender-identity/TeammateIdentity.tsx
 * FILE_ID: EMAILHUB-SENDERIDENTITY-TEAMMATE-001
 * PURPOSE: Render the internal-colleague identity states and their role picker.
 * ROLE IN ARCHITECTURE: Presentational leaf of the sender-identity module.
 * MAIN EXPORTS: TeammateIdentity, TeammateOverriddenIdentity.
 * NON-RESPONSIBILITIES: No gating (predicates.ts), no mutations (actions hook).
 *
 * NOTES FOR FUTURE AI: A teammate must never render a customer-CRM badge, even
 * when a stale Contact row carries their address — that was BUG-2026-07-09-
 * INTERNAL-SENDER-CRM (the org's own Operations Manager displayed as "Known
 * sender · John Smith"). The chip is deliberately ACTIONABLE: the operator
 * reported "tried clicking teammate… can't do it". Moved verbatim out of
 * SenderIdentityCard.tsx (change #302, size split).
 *
 * ``flat`` is the reader-lane variant, and it is the same contract the CRM and
 * vendor leaves already honour: no border, no fill, ONE row. This leaf was
 * missed when the flat variants landed, so inside the Company lane a teammate
 * still rendered its bordered card and its ``flex-wrap`` row wrapped onto two
 * or three lines — a card nested in a card, and the tallest thing in the
 * expanded context. Never let the flat branch grow a border, a wrap, or an
 * ``ml-auto``: at tablet width the lane is one 305px half, and each of those
 * three is what pushed the operator's Company lane out of shape.
 */
import { useRef } from 'react'
import { ShieldQuestion, User, Users } from 'lucide-react'

import { AdaptiveOverlay } from '@quotech/ui'
import { cn } from '../../../_internal/lib/utils'
import type { Email } from '../../hooks/useEmails.types'
import {
  SENDER_ROLES,
  type SenderIdentityActions,
  type SenderRoleKey,
} from './useSenderIdentityActions'

interface TeammateIdentityProps {
  email: Email
  actions: SenderIdentityActions
  /** Flat chip (reader lane) vs bordered pill (standalone reader). */
  flat: boolean
  /** Shared padding/type scale from the frame (card vs inline chip). */
  pad: string
  className?: string
}

/** The relationship buttons — one list, rendered inline (card) or in the
 *  flat variant's popover, so the two can never offer different choices. */
function RoleChoices({ actions }: { actions: SenderIdentityActions }) {
  return (
    <>
      {(Object.keys(SENDER_ROLES) as SenderRoleKey[]).map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => actions.overrideTeammate('set_role', key)}
          disabled={actions.busy}
          className="rounded-md border border-border px-1.5 py-0.5 text-[10.5px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          title={
            SENDER_ROLES[key].internal
              ? 'Stays internal — labels the chip and steers the AI'
              : 'Treated as external — resolves through the CRM'
          }
          data-testid={`teammate-role-${key}`}
        >
          {SENDER_ROLES[key].label}
        </button>
      ))}
      <button
        type="button"
        onClick={() => actions.overrideTeammate('not_teammate')}
        disabled={actions.busy}
        className="rounded-md border border-border px-1.5 py-0.5 text-[10.5px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
        title="Just not a teammate — no specific relationship"
        data-testid="teammate-not-teammate"
      >
        Not a teammate
      </button>
    </>
  )
}

/** Flat lane row: icon, name, role, Change — one line, no chrome. */
function FlatTeammateIdentity({
  teammateName,
  roleLabel,
  actions,
}: {
  teammateName: string
  roleLabel: string | null | undefined
  actions: SenderIdentityActions
}) {
  const changeRef = useRef<HTMLButtonElement>(null)
  return (
    <div
      className="flex min-w-0 items-center gap-1.5"
      data-testid="sender-identity-card"
      data-teammate="true"
    >
      {/* The word "Teammate" costs ~55px of a 305px half that also has to
          hold the name, the role and the Change control. The icon carries
          the state visually and the sr-only text carries it to everything
          else, so nothing is lost but the width. */}
      <Users
        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
        aria-hidden
      />
      <span className="sr-only">Teammate</span>
      {/* Half a lane is not much width — the name is the first thing to
          truncate, so the untruncated value has to stay reachable. */}
      <span
        className="truncate font-medium text-foreground/90"
        title={teammateName}
      >
        {teammateName}
      </span>
      {roleLabel && (
        <span className="shrink-0 text-[10.5px] text-muted-foreground">
          · {roleLabel}
        </span>
      )}
      <button
        ref={changeRef}
        type="button"
        onClick={() => actions.setRoleMenuOpen(!actions.roleMenuOpen)}
        disabled={actions.busy}
        className="shrink-0 inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10.5px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
        title="Change this sender's relationship — employee, contractor, client…"
        data-testid="teammate-change-role"
        aria-expanded={actions.roleMenuOpen}
        aria-haspopup="dialog"
      >
        <ShieldQuestion className="h-3 w-3 shrink-0" aria-hidden />
        Change
      </button>
      {/* The picker is six buttons. Inline, it was a second and third row
          inside a lane whose contract is one row; anchored, it costs the
          header nothing and closes itself. */}
      <AdaptiveOverlay
        open={actions.roleMenuOpen}
        onOpenChange={actions.setRoleMenuOpen}
        anchorRef={changeRef}
        label="Change sender relationship"
        testId="teammate-role-menu"
        popoverWidth={260}
      >
        <div className="flex flex-wrap items-center gap-1">
          <RoleChoices actions={actions} />
        </div>
      </AdaptiveOverlay>
      {actions.error && (
        <span className="shrink-0 text-destructive text-[10.5px]">{actions.error}</span>
      )}
    </div>
  )
}

export function TeammateIdentity({
  email,
  actions,
  flat,
  pad,
  className,
}: TeammateIdentityProps) {
  const teammateName =
    email.teammate_name || email.from_name || email.from_address?.split('@')[0] || 'Colleague'
  const roleLabel = actions.chosenRole
    ? SENDER_ROLES[actions.chosenRole].label
    : email.teammate_role

  if (flat) {
    return (
      <FlatTeammateIdentity
        teammateName={teammateName}
        roleLabel={roleLabel}
        actions={actions}
      />
    )
  }

  return (
    <div
      className={cn('rounded-lg border border-border bg-muted/50', pad, className)}
      data-testid="sender-identity-card"
      data-teammate="true"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Users className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
        <span className="text-foreground/80 font-medium">Teammate</span>
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <User className="h-3.5 w-3.5 shrink-0" aria-hidden /> {teammateName}
        </span>
        {roleLabel && <span className="text-muted-foreground">· {roleLabel}</span>}
        <button
          type="button"
          onClick={() => actions.setRoleMenuOpen(!actions.roleMenuOpen)}
          disabled={actions.busy}
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10.5px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          title="Change this sender's relationship — employee, contractor, client…"
          data-testid="teammate-change-role"
          aria-expanded={actions.roleMenuOpen}
        >
          <ShieldQuestion className="h-3 w-3 shrink-0" aria-hidden />
          Change
        </button>
      </div>
      {actions.roleMenuOpen && (
        <div
          className="mt-1.5 flex items-center gap-1 flex-wrap"
          data-testid="teammate-role-menu"
        >
          <RoleChoices actions={actions} />
        </div>
      )}
      {actions.error && (
        <span className="text-destructive text-[10.5px]">{actions.error}</span>
      )}
    </div>
  )
}

/** Just overridden (optimistic): confirm + offer Undo until the parent
 *  refetch lands with is_internal_sender=false. */
export function TeammateOverriddenIdentity({
  actions,
  flat,
  pad,
  className,
}: Omit<TeammateIdentityProps, 'email'>) {
  const label = actions.chosenRole
    ? `Marked as ${SENDER_ROLES[actions.chosenRole].label.toLowerCase()}`
    : 'Marked as external sender'

  if (flat) {
    return (
      <div
        className="flex min-w-0 items-center gap-1.5"
        data-testid="sender-identity-card"
        data-teammate-overridden="true"
      >
        <ShieldQuestion className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="truncate text-foreground/90">{label}</span>
        <button
          type="button"
          onClick={() => actions.overrideTeammate('undo')}
          disabled={actions.busy}
          className="shrink-0 rounded-md border border-border px-1.5 py-0.5 text-[10.5px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          data-testid="teammate-override-undo"
        >
          Undo
        </button>
        {actions.error && (
          <span className="shrink-0 text-destructive text-[10.5px]">{actions.error}</span>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 flex-wrap rounded-lg border border-border bg-muted/50',
        pad,
        className,
      )}
      data-testid="sender-identity-card"
      data-teammate-overridden="true"
    >
      <ShieldQuestion className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
      <span className="text-foreground/80">{label}</span>
      <button
        type="button"
        onClick={() => actions.overrideTeammate('undo')}
        disabled={actions.busy}
        className="ml-auto rounded-md border border-border px-1.5 py-0.5 text-[10.5px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
        data-testid="teammate-override-undo"
      >
        Undo
      </button>
      {actions.error && (
        <span className="text-destructive text-[10.5px]">{actions.error}</span>
      )}
    </div>
  )
}
